import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  Card,
  CardContent,
  TableBody,
  TablePagination,
} from '@material-ui/core'
import AttachFileIcon from '@material-ui/icons/AttachFile'
import CloseIcon from '@material-ui/icons/Close'
import {
  Button,
  Input,
  TextArea,
  Select,
  Table,
  TableHead,
  TableRow,
  TableCell,
} from 'sputnik-ui'
import FilterButton from 'components/atoms/FilterButton'
import palette from 'theme/palette'
import { exportAdminReleaseNotes, uploadReleaseNoteScreenshot } from '../api'
import {
  fetchAdminReleaseNotes,
  createAdminReleaseNote,
  updateAdminReleaseNote,
  publishAdminReleaseNote,
  archiveAdminReleaseNote,
  setAdminFilter,
  setAdminPage,
  setAdminPerPage,
  clearMessage,
} from '../model/releaseNotesSlice'
import {
  RELEASE_PRIORITIES,
  RELEASE_SCOPES,
  RELEASE_SEGMENTS,
} from '../constants'
import { trackReleaseEvent } from '../metrics'
import { formatReleaseDate } from '../utils'

const adminFilters = ['all', 'draft', 'published', 'archived']

const ALLOWED_SCREENSHOT_TYPES = ['image/jpeg', 'image/png']

const MESSAGE_KEYS = {
  updated: ['release_notes:admin.messages.updated', 'Релиз обновлен'],
  draftCreated: [
    'release_notes:admin.messages.draft_created',
    'Черновик релиза создан',
  ],
  aiCreated: ['release_notes:admin.ai.created', 'AI draft создан'],
  aiError: ['release_notes:admin.ai.error', 'Ошибка генерации AI draft'],
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  max-width: 1100px;
  margin: 0 auto;
`

const TableColumn = styled.div`
  min-width: 1000px;
`

const TableScrollArea = styled.div`
  height: 420px;
  overflow: auto;
`

const FormCard = styled(Card)``

const Filters = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`

const StatusBadge = styled.span`
  border-radius: 999px;
  font-size: 12px;
  padding: 3px 10px;
  color: ${(p) =>
    p.$status === 'published'
      ? palette.success.color
      : p.$status === 'scheduled'
      ? palette.warning.color
      : p.$status === 'archived'
      ? palette.neutral.color
      : palette.accent.color};
  background: ${(p) =>
    p.$status === 'published'
      ? palette.success.background
      : p.$status === 'scheduled'
      ? palette.warning.background
      : p.$status === 'archived'
      ? palette.neutral.background
      : palette.accent.background};
`

const FormTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 20px;
  color: #202020;
`

const Field = styled.div`
  margin-top: 14px;
`

const Label = styled.label`
  display: block;
  font-size: 13px;
  color: #6d6d6d;
  margin-bottom: 6px;
`

const SubmitRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const SubmitError = styled.span`
  font-size: 13px;
  color: ${palette.danger.color};
`

const Dropzone = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1px dashed ${(p) => (p.$dragOver ? palette.accent.color : '#d0d0d0')};
  border-radius: 6px;
  background: ${(p) =>
    p.$dragOver ? palette.accent.background : 'transparent'};
  transition: border-color 0.15s ease, background 0.15s ease;
`

const Thumb = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const RemoveThumbButton = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 12px;
    height: 12px;
  }
`

const AttachButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  background: #fff;
  color: ${palette.neutral.color};
  cursor: pointer;

  svg {
    width: 18px;
    height: 18px;
  }
`

const DropzoneHint = styled.span`
  font-size: 12px;
  color: ${palette.neutral.color};
`

const HiddenFileInput = styled.input`
  display: none;
`

const menuPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
}

const AdminConsole = () => {
  const { t } = useTranslation(['release_notes', 'time'])
  const dispatch = useDispatch()
  const list = useSelector(({ releaseNotes }) => releaseNotes.adminList)
  const filter = useSelector(({ releaseNotes }) => releaseNotes.adminFilter)
  const adminPage = useSelector(({ releaseNotes }) => releaseNotes.adminPage)
  const adminPerPage = useSelector(
    ({ releaseNotes }) => releaseNotes.adminPerPage
  )
  const adminTotalCount = useSelector(
    ({ releaseNotes }) => releaseNotes.adminTotalCount
  )
  const message = useSelector(({ releaseNotes }) => releaseNotes.message)
  const [form, setForm] = useState({
    title: '',
    summary: '',
    body: '',
    productScope: 'both',
    priority: 'medium',
  })
  const [selectedId, setSelectedId] = useState(null)
  const [submitError, setSubmitError] = useState(null)
  const [selectedSegments, setSelectedSegments] = useState(RELEASE_SEGMENTS)
  const [exporting, setExporting] = useState(false)
  const [screenshotUrls, setScreenshotUrls] = useState([])
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false)
  const [screenshotError, setScreenshotError] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef(null)

  const uploadScreenshots = async (files) => {
    const images = Array.from(files).filter((file) =>
      ALLOWED_SCREENSHOT_TYPES.includes(file.type)
    )
    if (!images.length) {
      setScreenshotError(
        t(
          'release_notes:admin.form.screenshots.invalid_type',
          'Допустимы только файлы JPG и PNG'
        )
      )
      return
    }
    setScreenshotError(null)
    setUploadingScreenshot(true)
    try {
      const uploadedUrls = await Promise.all(
        images.map((file) => uploadReleaseNoteScreenshot(file))
      )
      setScreenshotUrls((prev) => [...prev, ...uploadedUrls])
    } catch (err) {
      setScreenshotError(
        t(
          'release_notes:admin.form.screenshots.upload_error',
          'Не удалось загрузить скриншот'
        )
      )
    } finally {
      setUploadingScreenshot(false)
    }
  }

  const handleFileInputChange = (e) => {
    if (e.target.files?.length) uploadScreenshots(e.target.files)
    e.target.value = ''
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files?.length) uploadScreenshots(e.dataTransfer.files)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setDragOver(false)
  }

  const removeScreenshot = (url) =>
    setScreenshotUrls((prev) => prev.filter((u) => u !== url))

  useEffect(() => {
    dispatch(fetchAdminReleaseNotes())
  }, [dispatch, filter, adminPage, adminPerPage])

  useEffect(() => {
    return () => dispatch(clearMessage())
  }, [dispatch])

  const handleChangePage = (event, newPage) =>
    dispatch(setAdminPage(newPage + 1))

  const handleChangeRowsPerPage = (event) =>
    dispatch(setAdminPerPage(parseInt(event.target.value, 10)))

  const handleExport = async () => {
    setExporting(true)
    try {
      const blob = await exportAdminReleaseNotes(selectedId)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `release_note_${selectedId}.csv`
      link.click()
      URL.revokeObjectURL(url)
    } finally {
      setExporting(false)
    }
  }

  const scopeOptions = Object.values(RELEASE_SCOPES).map((value) => ({
    value,
    label: t(`release_notes:scope.${value}`),
  }))

  const displaySegmentOptions = (values) => {
    if (!values) {
      return []
    }
    return Object.values(values).map((value) => ({
      value,
      label: value,
    }))
  }

  const priorityOptions = Object.values(RELEASE_PRIORITIES).map((value) => ({
    value,
    label: t(`release_notes:priority.${value}`),
  }))

  const submitForm = async () => {
    /* eslint-disable camelcase */
    const payload = {
      title: form.title,
      summary: form.summary,
      body: form.body,
      product_scope: form.productScope,
      priority: form.priority,
      audience_roles: ['all'],
      audience_segments: selectedSegments,
      screenshots: screenshotUrls,
    }
    /* eslint-enable camelcase */

    setSubmitError(null)
    try {
      if (selectedId) {
        await dispatch(
          updateAdminReleaseNote({ id: selectedId, payload })
        ).unwrap()
        trackReleaseEvent('admin_update', { releaseId: selectedId })
      } else {
        await dispatch(
          createAdminReleaseNote({ ...payload, status: 'draft' })
        ).unwrap()
        trackReleaseEvent('admin_create', {
          productScope: form.productScope,
          priority: form.priority,
        })
      }
    } catch (err) {
      setSubmitError(
        err ||
          t(
            'release_notes:admin.form.submit_error',
            'Не удалось сохранить релиз'
          )
      )
      return
    }
    setForm({
      title: '',
      summary: '',
      body: '',
      productScope: 'both',
      priority: 'medium',
      audience_segments: [],
    })
    setSelectedId(null)
    setScreenshotUrls([])
    setScreenshotError(null)
  }

  const handleRowClick = (item) => {
    setSelectedId(item.id)
    setSelectedSegments(item.audience_segments)
    setSubmitError(null)
    setForm({
      title: item.title || '',
      summary: item.summary || '',
      body: item.body || '',
      productScope: item.productScope || 'both',
      priority: item.priority || 'medium',
    })
    setScreenshotUrls(item.screenshots || [])
    setScreenshotError(null)
  }

  return (
    <Page>
      <Filters>
        {adminFilters.map((value) => (
          <FilterButton
            key={value}
            selected={filter === value}
            onClick={() => dispatch(setAdminFilter(value))}
          >
            {t(`release_notes:admin.filter.${value}`)}
          </FilterButton>
        ))}
      </Filters>

      <TableColumn>
        <TableScrollArea>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('release_notes:admin.table.title')}</TableCell>
                <TableCell>{t('release_notes:admin.table.product')}</TableCell>
                <TableCell>{t('release_notes:admin.table.status')}</TableCell>
                <TableCell>{t('release_notes:admin.table.date')}</TableCell>
                <TableCell>{t('release_notes:admin.table.reach')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((item) => (
                <TableRow
                  key={item.id}
                  style={{
                    cursor: 'pointer',
                    backgroundColor:
                      selectedId === item.id
                        ? palette.accent.background
                        : 'transparent',
                  }}
                  onClick={() => handleRowClick(item)}
                >
                  <TableCell>
                    {item.title.length > 30
                      ? `${item.title.slice(0, 30)}...`
                      : item.title}
                  </TableCell>
                  <TableCell>
                    {t(`release_notes:scope.${item.productScope}`) ||
                      item.productScope}
                  </TableCell>
                  <TableCell>
                    <StatusBadge $status={item.status}>
                      {t(`release_notes:admin.status.${item.status}`) ||
                        item.status}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    {formatReleaseDate(item.publishedAt, t)}
                  </TableCell>
                  <TableCell>{`${item.total_reads}`}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScrollArea>
        <TablePagination
          component="div"
          count={adminTotalCount}
          page={adminPage - 1}
          rowsPerPage={adminPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t(
            'release_notes:admin.table.rows_per_page',
            'Строк на странице:'
          )}
          labelDisplayedRows={({ from, to, count }) =>
            t('release_notes:admin.table.displayed_rows', {
              defaultValue: '{{from}}-{{to}} из {{count}}',
              from,
              to,
              count,
            })
          }
        />
      </TableColumn>

      <FormCard>
        <CardContent>
          <FormTitle>
            {selectedId
              ? t('release_notes:admin.form.update_title', 'Обновить релиз')
              : t('release_notes:admin.form.create_title', 'Создать релиз')}
          </FormTitle>
          {message && (
            <div
              style={{
                fontSize: 13,
                color: palette.accent.color,
                marginBottom: 8,
              }}
            >
              {t(...MESSAGE_KEYS[message])}
            </div>
          )}
          <Field>
            <Label>
              {t('release_notes:admin.form.title_label', 'Заголовок')}
            </Label>
            <Input
              fullWidth
              placeholder={t(
                'release_notes:admin.form.title_placeholder',
                'Введите заголовок'
              )}
              value={form.title}
              onChange={(e) =>
                setForm((p) => ({ ...p, title: e.target.value }))
              }
            />
          </Field>
          <Field>
            <Label>
              {t('release_notes:admin.form.summary_label', 'Краткое описание')}
            </Label>
            <Input
              fullWidth
              placeholder={t(
                'release_notes:admin.form.summary_placeholder',
                'Одним предложением о чём релиз'
              )}
              value={form.summary}
              onChange={(e) =>
                setForm((p) => ({ ...p, summary: e.target.value }))
              }
            />
          </Field>
          <Field>
            <Label>
              {t('release_notes:admin.form.body_label', 'Полный текст')}
            </Label>
            <TextArea
              maxLength="4000"
              placeholder={t(
                'release_notes:admin.form.body_placeholder',
                'Опишите изменения подробно...'
              )}
              value={form.body}
              onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
            />
          </Field>
          <Field>
            <Label>
              {t('release_notes:admin.form.screenshots.label', 'Скриншоты')}
            </Label>
            <Dropzone
              $dragOver={dragOver}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {screenshotUrls.map((url) => (
                <Thumb key={url}>
                  <img src={url} alt="" />
                  <RemoveThumbButton
                    type="button"
                    onClick={() => removeScreenshot(url)}
                    aria-label={t(
                      'release_notes:admin.form.screenshots.remove',
                      'Удалить скриншот'
                    )}
                  >
                    <CloseIcon />
                  </RemoveThumbButton>
                </Thumb>
              ))}
              <AttachButton
                type="button"
                disabled={uploadingScreenshot}
                onClick={() => fileInputRef.current?.click()}
              >
                <AttachFileIcon />
                <span>
                  {uploadingScreenshot
                    ? t(
                        'release_notes:admin.form.screenshots.uploading',
                        'Загрузка...'
                      )
                    : t(
                        'release_notes:admin.form.screenshots.attach',
                        'Прикрепить'
                      )}
                </span>
              </AttachButton>
              <DropzoneHint>
                {t(
                  'release_notes:admin.form.screenshots.hint',
                  'Перетащите JPG/PNG сюда или нажмите на скрепку'
                )}
              </DropzoneHint>
              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                multiple
                onChange={handleFileInputChange}
              />
            </Dropzone>
            {screenshotError && <SubmitError>{screenshotError}</SubmitError>}
          </Field>
          <Field>
            <Label>
              {t('release_notes:admin.form.product_label', 'Продукт')}
            </Label>
            <Select
              options={scopeOptions}
              value={scopeOptions.find((o) => o.value === form.productScope)}
              handleSelect={(option) =>
                setForm((p) => ({ ...p, productScope: option.value }))
              }
              menuPortalTarget={document.querySelector('body')}
              styles={menuPortalStyles}
              isMulti={false}
              isSearchable={false}
            />
          </Field>
          <Field>
            <Label>{t('release_notes:admin.form.segment_label')}</Label>
            <Select
              options={displaySegmentOptions(RELEASE_SEGMENTS)}
              value={displaySegmentOptions(selectedSegments)}
              handleSelect={(option) =>
                setSelectedSegments(option.map((i) => i.value))
              }
              menuPortalTarget={document.querySelector('body')}
              styles={menuPortalStyles}
              isMulti
              isSearchable={false}
            />
          </Field>
          <Field>
            <Label>
              {t('release_notes:admin.form.priority_label', 'Приоритет')}
            </Label>
            <Select
              options={priorityOptions}
              value={priorityOptions.find((o) => o.value === form.priority)}
              handleSelect={(option) =>
                setForm((p) => ({ ...p, priority: option.value }))
              }
              menuPortalTarget={document.querySelector('body')}
              styles={menuPortalStyles}
              isMulti={false}
              isSearchable={false}
            />
          </Field>
          <Field>
            <SubmitRow>
              <Button primary variant="v2" onClick={submitForm}>
                {selectedId
                  ? t('release_notes:admin.form.save', 'Сохранить')
                  : t('release_notes:admin.form.create_draft', 'Создать draft')}
              </Button>
              {submitError && <SubmitError>{submitError}</SubmitError>}
              {selectedId && (
                <>
                  <Button
                    size="small"
                    variant="v2"
                    onClick={() => {
                      dispatch(publishAdminReleaseNote(selectedId))
                      trackReleaseEvent('admin_publish', {
                        releaseId: selectedId,
                      })
                    }}
                  >
                    {t('release_notes:admin.actions.publish', 'Опубликовать')}
                  </Button>
                  <Button
                    size="small"
                    variant="v2"
                    tertiary
                    onClick={() => {
                      dispatch(archiveAdminReleaseNote(selectedId))
                      trackReleaseEvent('admin_archive', {
                        releaseId: selectedId,
                      })
                    }}
                  >
                    {t('release_notes:admin.actions.archive', 'Архивировать')}
                  </Button>

                  <Button
                    size="small"
                    variant="v2"
                    tertiary
                    disabled={exporting}
                    onClick={handleExport}
                  >
                    {t('release_notes:admin.actions.export', 'Экспорт CSV')}
                  </Button>
                </>
              )}
            </SubmitRow>
          </Field>
        </CardContent>
      </FormCard>
    </Page>
  )
}

export default AdminConsole
